import PaySonicLogo from "@assets/logos/PaySonic.svg";
import TyltLogo from "@assets/logos/tylt_logo.png";
import LogoutSlidingPanel from "@components/sliders/LogoutSlidingPanel";
import webSocketService from "@services/websocket/WebsocketServices";
import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import {
  isPathAllowedForPaysonic,
  isPathAllowedForPrimeBrazilOnly,
  isPaysonicHost,
  isPrimeBrazilOnlyHost,
} from "src/config/deploymentConfig";
import {
  ADMIN_NAV,
  filterNav,
  isNavItemActive,
  NavItem,
  NavSection,
} from "./adminNav";

const visibleSections = (): NavSection[] => {
  if (isPrimeBrazilOnlyHost()) {
    return ADMIN_NAV.map((section) => ({
      ...section,
      items: section.items.filter((item) =>
        isPathAllowedForPrimeBrazilOnly(item.path),
      ),
    })).filter((section) => section.items.length > 0);
  }

  if (isPaysonicHost()) {
    return ADMIN_NAV.map((section) => ({
      ...section,
      items: section.items.filter((item) => isPathAllowedForPaysonic(item.path)),
    })).filter((section) => section.items.length > 0);
  }

  return ADMIN_NAV;
};

export default function MobileLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [primeDisputeNotification, setPrimeDisputeNotification] =
    useState(false);
  const currentPath = location.pathname || "/";
  const pathRef = useRef(currentPath);
  pathRef.current = currentPath;
  const isPrimeBrazilOnly = isPrimeBrazilOnlyHost();
  const isPaysonic = isPaysonicHost();
  const brandLogo = isPaysonic ? PaySonicLogo : TyltLogo;

  useEffect(() => {
    try {
      webSocketService.onMessage("admin_trade_update_dispute", true, () => {
        if (!pathRef.current.startsWith("/prime-dispute-resolution")) {
          setPrimeDisputeNotification(true);
        }
      });
    } catch (error) {
      console.error("WebSocket initialization error:", error);
    }
  }, [webSocketService.protectedSocket?.connected]);

  const sections = useMemo(() => filterNav(visibleSections(), query), [query]);

  const navigateTo = (item: NavItem) => {
    if (item.notify === "disputes") setPrimeDisputeNotification(false);
    navigate(item.path);
    setDrawerOpen(false);
  };

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-bg-page-body">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-border-subtle bg-[#FFFFFF] px-3">
        <button
          type="button"
          aria-label="Open navigation"
          className="flex h-10 w-10 items-center justify-center rounded-lg text-[#3A4158] hover:bg-[#F4F6F8]"
          onClick={() => setDrawerOpen(true)}
        >
          <span className="material-symbols-outlined text-[24px]">menu</span>
        </button>
        {isPrimeBrazilOnly ? (
          <div className="h-9 w-9" />
        ) : (
          <img
            src={brandLogo}
            alt="Logo"
            className={`object-contain ${isPaysonic ? "h-5 max-w-[110px]" : "h-9 max-w-[110px]"}`}
          />
        )}
        <button
          type="button"
          aria-label="Logout"
          className="flex h-10 w-10 items-center justify-center rounded-lg text-[#3A4158] hover:bg-[#F4F6F8]"
          onClick={() => setLogoutOpen(true)}
        >
          <span className="material-symbols-outlined text-[20px]">logout</span>
        </button>
      </header>

      {drawerOpen && (
        <button
          type="button"
          aria-label="Close navigation overlay"
          className="fixed inset-0 z-40 bg-black/50"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-[280px] max-w-[85vw] flex-col border-r border-border-subtle bg-[#FFFFFF] shadow-xl transition-transform duration-300 ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-14 items-center justify-between border-b border-border-subtle px-4">
          {isPrimeBrazilOnly ? (
            <div className="h-9" />
          ) : (
            <img
              src={brandLogo}
              alt="Logo"
              className={`object-contain ${isPaysonic ? "h-5 max-w-[120px]" : "h-9 max-w-[120px]"}`}
            />
          )}
          <button
            type="button"
            aria-label="Close navigation"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-[#3A4158]"
            onClick={() => setDrawerOpen(false)}
          >
            <span className="material-symbols-outlined text-[22px]">close</span>
          </button>
        </div>

        <div className="px-3 pt-3">
          <label className="relative block">
            <span className="material-symbols-outlined pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[16px] text-[#686E92]">
              search
            </span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search"
              className="h-8 w-full rounded-lg border border-[#D7DEE8] bg-[#F7F8FA] pl-8 pr-2 text-[12px] font-medium text-[#15182C] outline-none placeholder:text-[#686E92] focus:border-[#7EC8EA] focus:bg-[#FFFFFF]"
            />
          </label>
        </div>

        <nav className="flex flex-1 flex-col gap-4 overflow-y-auto p-3">
          {sections.length === 0 ? (
            <p className="px-2.5 text-[12px] text-text-subtle">
              No pages match “{query.trim()}”.
            </p>
          ) : (
            sections.map((section, sectionIndex) => (
              <div key={section.title} className="flex flex-col gap-1.5">
                <span className="px-2.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-subtle">
                  {section.title}
                </span>
                <div className="flex flex-col gap-0.5">
                  {section.items.map((item) => {
                    const active = isNavItemActive(currentPath, item.path);
                    const showAlert =
                      item.notify === "disputes" &&
                      primeDisputeNotification &&
                      !currentPath.startsWith("/prime-dispute-resolution");
                    return (
                      <button
                        key={item.path}
                        type="button"
                        onClick={() => navigateTo(item)}
                        className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium transition-colors ${
                          active
                            ? "bg-[#E8F6FC] text-[#2436C7]"
                            : "text-[#3A4158] hover:bg-[#F4F6F8]"
                        }`}
                      >
                        <span className="material-symbols-outlined text-[18px] leading-none">
                          {item.icon}
                        </span>
                        <span className="min-w-0 flex-1 truncate">
                          {item.label}
                        </span>
                        {showAlert && (
                          <span className="h-2 w-2 shrink-0 rounded-full bg-[#D61C1C]" />
                        )}
                      </button>
                    );
                  })}
                </div>
                {sectionIndex < sections.length - 1 && (
                  <div className="mx-2 mt-2 h-px bg-border-subtle" />
                )}
              </div>
            ))
          )}
        </nav>
      </aside>

      <main className="min-h-0 flex-1 overflow-auto">
        <Outlet />
      </main>

      <LogoutSlidingPanel
        isOpen={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        onClickConfirm={() => {
          secureLocalStorage.clear();
          navigate("/login");
        }}
        sliderHeading="Logout"
      />
    </div>
  );
}
